import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardMedia,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  uploadRestaurantPhoto,
  uploadLogoPhoto,
  deleteRestaurantPhoto
} from '../../services/restaurantService';

function PhotoUploader({
  currentPhoto,
  photoList = [],
  onPhotoChange,
  onPhotosChange,
  restaurantId,
  isLogo = false,
  multiple = false
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleUpload = async (event) => {
    if (!event.target.files || event.target.files.length === 0) return;
    
    if (!restaurantId) {
      setError('Please save the restaurant first before uploading photos');
      return;
    }
    
    setUploading(true);
    setError(null);
    
    try {
      if (isLogo) {
        // Single logo upload
        const file = event.target.files[0];
        const downloadURL = await uploadLogoPhoto(restaurantId, file);
        onPhotoChange(downloadURL);
      } else if (multiple) {
        // Multiple photos upload
        const files = Array.from(event.target.files);
        const uploadPromises = files.map(file => 
          uploadRestaurantPhoto(restaurantId, file)
        );
        
        const downloadURLs = await Promise.all(uploadPromises);
        onPhotosChange([...photoList, ...downloadURLs]);
      } else {
        // Single photo upload
        const file = event.target.files[0];
        const downloadURL = await uploadRestaurantPhoto(restaurantId, file);
        onPhotosChange([...photoList, downloadURL]);
      }
      
      setSuccess(true);
    } catch (err) {
      console.error('Error uploading photo:', err);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDelete = async (photoUrl) => {
    if (!restaurantId) return;
    
    try {
      await deleteRestaurantPhoto(restaurantId, photoUrl);
      
      if (isLogo) {
        onPhotoChange('');
      } else {
        const updatedPhotos = photoList.filter(url => url !== photoUrl);
        onPhotosChange(updatedPhotos);
      }
      
      setSuccess(true);
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to delete photo. Please try again.');
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(false);
  };

  return (
    <Box>
      {isLogo ? (
        // Single logo display and upload
        <Box sx={{ mb: 2 }}>
          {currentPhoto ? (
            <Card sx={{ maxWidth: 250, mb: 2 }}>
              <CardMedia
                component="img"
                height="150"
                image={currentPhoto}
                alt="Restaurant Logo"
              />
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton 
                  color="error" 
                  size="small"
                  onClick={() => handleDelete(currentPhoto)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Card>
          ) : (
            <Box 
              sx={{ 
                width: 250, 
                height: 150, 
                border: '2px dashed #ccc',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
                borderRadius: 1
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No logo uploaded
              </Typography>
            </Box>
          )}
          
          <Button
            component="label"
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            disabled={uploading || !restaurantId}
          >
            {uploading ? 'Uploading...' : 'Upload Logo'}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleUpload}
            />
          </Button>
        </Box>
      ) : (
        // Multiple photos display and upload
        <Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {photoList.length > 0 ? (
              photoList.map((photo, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="150"
                      image={photo}
                      alt={`Restaurant photo ${index + 1}`}
                    />
                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton 
                        color="error" 
                        size="small"
                        onClick={() => handleDelete(photo)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Box 
                  sx={{ 
                    height: 150, 
                    border: '2px dashed #ccc',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No photos uploaded
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
          
          <Button
            component="label"
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            disabled={uploading || !restaurantId}
          >
            {uploading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Uploading...
              </>
            ) : (
              `Upload Photo${multiple ? 's' : ''}`
            )}
            <input
              type="file"
              hidden
              accept="image/*"
              multiple={multiple}
              onChange={handleUpload}
            />
          </Button>
        </Box>
      )}

      {/* Error/Success messages */}
      <Snackbar 
        open={!!error || success} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? 'error' : 'success'} 
          variant="filled"
        >
          {error || 'Photo updated successfully'}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PhotoUploader;